import { useMutation, useQuery } from '@tanstack/react-query';
import CatalogService from '@utility/services/catalog';
import { ErrorMessageConstant } from '@utility/constants/common';
import CatalogSettingsService from '@utility/services/catalog-settings';

/*
 * Discontinuing an item and the catalog search suggestions, as the legacy
 * catalog screens read them. On react-query; each hook keeps the shape its
 * callers destructure.
 */

/* A shared empty list, so callers that memoize on it see one reference. */
// biome-ignore lint/suspicious/noExplicitAny: legacy screens type the rows themselves.
const NO_ROWS: any[] = [];

export const catalogHookKeys = {
  autocomplete: (search: string, regionId: unknown) =>
    ['catalog-autocomplete', search, regionId] as const,
};

const apiError = (
  error: unknown,
  fallback = ErrorMessageConstant.defaultAPIError,
) =>
  (error as { message?: string; error?: string } | null)?.message ||
  (error as { error?: string } | null)?.error ||
  (error ? fallback : '');

/** Discontinues an item. */
export const UpdateDiscontinuedItemsHook = () => {
  const mutation = useMutation({
    mutationFn: (id: string) =>
      CatalogSettingsService.updateDiscontinuedItems(id),
  });

  return {
    data: mutation.data ?? '',
    loading: mutation.isPending,
    error: apiError(mutation.error),
    /* Callers read the outcome from `data` and `error`, never a rejection. */
    updateItem: (id: string) => mutation.mutateAsync(id).catch(() => {}),
  };
};

export const isValidCatalogSearch = search =>
  !!(search && typeof search === 'string' && search.trim().length >= 3);

/** Catalog suggestions for a search of three characters or more. */
export const catalogAutocompleteHook = (search, regionId) => {
  const valid = isValidCatalogSearch(search);

  const query = useQuery({
    queryKey: catalogHookKeys.autocomplete(search, regionId),
    queryFn: async ({ signal }) => {
      const result = await CatalogService.getAutocomplete(
        { search, marketplace_region_id: regionId },
        signal,
      );
      return (result?.data ?? NO_ROWS).map(row => ({ ...row, search }));
    },
    enabled: valid,
  });

  return {
    data: valid ? (query.data ?? NO_ROWS) : NO_ROWS,
    error: apiError(query.error),
    loading: query.isFetching,
  };
};
